import { useState } from 'react';
import { 
  Search,
  Filter,
  CheckCircle,
  XCircle,
  HelpCircle,
  Clock,
  Shield,
  ArrowUp,
  Pause,
  LayoutList,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Approval, ApprovalType, ApprovalStatus } from '@/types/supervisor';

const approvals: Approval[] = [];
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export function ApprovalWorkflows() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'reject' | 'more_info'>('approve');
  const [decisionNotes, setDecisionNotes] = useState('');
  const { toast } = useToast();

  const filteredApprovals = approvals
    .filter(approval => {
      const matchesSearch = 
        approval.ticketTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        approval.ticketId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        approval.requestor.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || approval.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || approval.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by: pending first, then by SLA due date
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      if (a.slaDueAt && b.slaDueAt) {
        return new Date(a.slaDueAt).getTime() - new Date(b.slaDueAt).getTime();
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const pendingCount = approvals.filter(a => a.status === 'pending').length;

  const getTypeIcon = (type: ApprovalType) => {
    switch (type) {
      case 'resolution': return <CheckCircle className="h-4 w-4" />;
      case 'access_request': return <Shield className="h-4 w-4" />;
      case 'priority_change': return <ArrowUp className="h-4 w-4" />;
      case 'sla_pause': return <Pause className="h-4 w-4" />;
      case 'bulk_action': return <LayoutList className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: ApprovalType) => {
    switch (type) {
      case 'resolution': return 'Resolution';
      case 'access_request': return 'Access Request';
      case 'priority_change': return 'Priority Change';
      case 'sla_pause': return 'SLA Pause';
      case 'bulk_action': return 'Bulk Action';
    }
  };

  const getTypeColor = (type: ApprovalType) => {
    switch (type) {
      case 'resolution': return 'bg-success/10 text-success border-success';
      case 'access_request': return 'bg-primary/10 text-primary border-primary';
      case 'priority_change': return 'bg-warning/10 text-warning border-warning';
      case 'sla_pause': return 'bg-muted text-muted-foreground border-muted-foreground';
      case 'bulk_action': return 'bg-accent text-accent-foreground border-accent';
    }
  };

  const getStatusBadge = (status: ApprovalStatus) => {
    const config = {
      pending: { class: 'bg-warning/20 text-warning border-warning', icon: Clock },
      approved: { class: 'bg-success/20 text-success border-success', icon: CheckCircle },
      rejected: { class: 'bg-destructive/20 text-destructive border-destructive', icon: XCircle },
      more_info_requested: { class: 'bg-primary/20 text-primary border-primary', icon: HelpCircle },
    };
    return config[status];
  };

  const handleDecision = (approval: Approval, action: 'approve' | 'reject' | 'more_info') => {
    setSelectedApproval(approval);
    setDecision(action);
    setDecisionNotes('');
    setDecisionDialogOpen(true);
  };

  const submitDecision = () => {
    const messages = {
      approve: 'Request Approved',
      reject: 'Request Rejected',
      more_info: 'More Information Requested',
    };
    toast({
      title: messages[decision],
      description: `${selectedApproval?.type.replace('_', ' ')} request from ${selectedApproval?.requestor} has been processed.`,
    });
    setDecisionDialogOpen(false);
    setSelectedApproval(null);
  };

  const getTimeRemaining = (dueAt: string) => {
    const now = new Date();
    const due = new Date(dueAt);
    const diff = due.getTime() - now.getTime();
    
    if (diff < 0) return { text: 'Overdue', isOverdue: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h`, isOverdue: false };
    }
    
    return { text: `${hours}h ${minutes}m`, isOverdue: false };
  };

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 animate-fade-in sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5 sm:space-y-1">
          <h1 className="text-xl font-bold flex items-center gap-2 sm:text-2xl">
            <CheckCircle className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
            Pending Approvals
          </h1>
          <p className="text-sm text-muted-foreground sm:text-base">Review and process approval requests</p>
        </div>
        <Badge variant="destructive" className="text-sm px-3 py-1">
          {pendingCount} pending
        </Badge>
      </div>

      {/* Filters */}
      <Card className="border-2">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search approvals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="resolution">Resolution</SelectItem>
                <SelectItem value="access_request">Access Request</SelectItem>
                <SelectItem value="priority_change">Priority Change</SelectItem>
                <SelectItem value="sla_pause">SLA Pause</SelectItem>
                <SelectItem value="bulk_action">Bulk Action</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="more_info_requested">More Info Requested</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Approval List */}
      <div className="space-y-3">
        {filteredApprovals.map((approval) => {
          const statusConfig = getStatusBadge(approval.status);
          const StatusIcon = statusConfig.icon;
          const timeRemaining = approval.slaDueAt ? getTimeRemaining(approval.slaDueAt) : null;
          
          return (
            <Card 
              key={approval.id} 
              className={cn(
                "border-2 transition-all hover:border-primary/50",
                approval.status === 'pending' && timeRemaining?.isOverdue && "border-l-4 border-l-destructive"
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Type Icon */}
                  <div className={cn(
                    "p-2 rounded-lg shrink-0",
                    getTypeColor(approval.type)
                  )}>
                    {getTypeIcon(approval.type)}
                  </div>

                  {/* Main Info */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={getTypeColor(approval.type)}>
                        {getTypeLabel(approval.type)}
                      </Badge>
                      <Badge variant="outline" className={statusConfig.class}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {approval.status.replace('_', ' ')}
                      </Badge>
                      {approval.ticketId && (
                        <span className="font-mono text-xs text-muted-foreground">
                          {approval.ticketId}
                        </span>
                      )}
                    </div>
                    
                    {approval.ticketTitle && (
                      <h3 className="font-semibold text-sm">{approval.ticketTitle}</h3>
                    )}
                    
                    {/* Request Details */}
                    <div className="bg-muted/50 p-3 rounded-lg space-y-2 text-sm">
                      {approval.requestData.currentValue && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Current:</span>
                          <Badge variant="outline" className="text-xs">{approval.requestData.currentValue}</Badge>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          <Badge variant="outline" className="text-xs border-primary text-primary">
                            {approval.requestData.requestedValue}
                          </Badge>
                        </div>
                      )}
                      {!approval.requestData.currentValue && approval.requestData.requestedValue && (
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground shrink-0">Request:</span>
                          <span className="font-medium">{approval.requestData.requestedValue}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground shrink-0">Justification:</span>
                        <span>{approval.requestData.justification}</span>
                      </div>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        Requested by <span className="font-medium">{approval.requestor}</span>
                      </span>
                      <span>•</span>
                      <span>{approval.requestorDepartment}</span>
                      <span>•</span>
                      <span>{new Date(approval.createdAt).toLocaleString()}</span>
                      {timeRemaining && approval.status === 'pending' && (
                        <>
                          <span>•</span>
                          <span className={cn(
                            "flex items-center gap-1",
                            timeRemaining.isOverdue && "text-destructive font-medium"
                          )}>
                            <Clock className="h-3 w-3" />
                            {timeRemaining.isOverdue ? 'Overdue' : `Due in ${timeRemaining.text}`}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Decision Info (for non-pending) */}
                    {approval.status !== 'pending' && approval.decisionNotes && (
                      <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
                        <span className="font-medium">Decision Notes:</span> {approval.decisionNotes}
                        {approval.decidedAt && (
                          <span className="ml-2">
                            ({new Date(approval.decidedAt).toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {approval.status === 'pending' && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button 
                        size="sm"
                        className="text-xs bg-success hover:bg-success/90"
                        onClick={() => handleDecision(approval, 'approve')}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        size="sm"
                        variant="destructive"
                        className="text-xs"
                        onClick={() => handleDecision(approval, 'reject')}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        Reject
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => handleDecision(approval, 'more_info')}
                      >
                        <HelpCircle className="h-3 w-3 mr-1" />
                        More Info
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Decision Dialog */}
      <Dialog open={decisionDialogOpen} onOpenChange={setDecisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision === 'approve' && <CheckCircle className="h-5 w-5 text-success" />}
              {decision === 'reject' && <XCircle className="h-5 w-5 text-destructive" />}
              {decision === 'more_info' && <HelpCircle className="h-5 w-5 text-primary" />}
              {decision === 'approve' && 'Approve Request'}
              {decision === 'reject' && 'Reject Request'}
              {decision === 'more_info' && 'Request More Information'}
            </DialogTitle>
            <DialogDescription>
              {getTypeLabel(selectedApproval?.type || 'resolution')} request from {selectedApproval?.requestor}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {decision === 'approve' && 'Approval Notes (optional)'}
                {decision === 'reject' && 'Rejection Reason (required)'}
                {decision === 'more_info' && 'What information do you need?'}
              </label>
              <Textarea
                placeholder={
                  decision === 'approve' ? 'Add any notes about this approval...' :
                  decision === 'reject' ? 'Explain why this request is being rejected...' :
                  'Specify what additional information is needed...'
                }
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitDecision}
              className={cn(
                decision === 'approve' && "bg-success hover:bg-success/90",
                decision === 'reject' && "bg-destructive hover:bg-destructive/90"
              )}
              disabled={decision === 'reject' && !decisionNotes.trim()}
            >
              {decision === 'approve' && 'Confirm Approval'}
              {decision === 'reject' && 'Confirm Rejection'}
              {decision === 'more_info' && 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
